import { Fragment, useMemo } from "react";

import { BudgetCells, BudgetSummaryCells } from "@app/lib/budgets/budget-cells";
import type { revenuePage_Query$data } from "@app/queries/revenuePage_Query.graphql";
import { CellLink } from "@app/components/ui/cell-link";

export type RevenueRow = revenuePage_Query$data["revenueByAccount"][number];

// The grid row identity a revenue budget keys on (matches the export + save).
export const acctRowKey = (bankAccountId: string) => `acct:${bankAccountId}`;

// The scope a clicked cell drills into — cell-scoped, so the panel's transaction
// list sums to exactly the amount that was clicked. Accounts are keyed by their
// Relay id (not number, which can collide across accounts).
export type CellScope =
  | { kind: "account-month"; accountId: string; label: string; month: string }
  | { kind: "account-year"; accountId: string; label: string }
  | { kind: "all-month"; label: string; month: string }
  | { kind: "all-year"; label: string };

const MONTH_LABELS = [
  "Jan", "Feb", "Mar", "Apr", "May", "Jun",
  "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
];

const money = (value: number) =>
  value.toLocaleString("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 0,
  });

const monthLabel = (yyyymm: string) =>
  MONTH_LABELS[Number(yyyymm.slice(5, 7)) - 1] ?? yyyymm;

const accountLabel = (account: RevenueRow["bankAccount"]) =>
  account.last4 ? `${account.nickname} ••${account.last4}` : account.nickname;

// The revenue audit grid: one row per internal account that received revenue in
// the selected year, 12 month columns, a per-account year-total column, and a
// bottom Totals row whose column sums equal the app-wide canonical revenue. Every
// positive cell drills into its transactions via onCell. In "Actuals vs Planned"
// mode each per-account month cell gains an editable budget + delta; the year and
// all-accounts rows show the rolled-up budget sums.
export default function RevenueGrid({
  rows,
  onCell,
  budgetMode = false,
  getBudgetText,
  onBudgetChange,
  getBudgetDollars,
}: {
  rows: readonly RevenueRow[];
  onCell: (scope: CellScope) => void;
  budgetMode?: boolean;
  getBudgetText?: (rowKey: string, period: string) => string;
  onBudgetChange?: (rowKey: string, period: string, text: string) => void;
  getBudgetDollars?: (rowKey: string, period: string) => number | null;
}) {
  // The 12 "YYYY-MM" labels, in order, from any row (every row carries Jan–Dec).
  const months = rows[0]?.points.map((point) => point.month) ?? [];

  const monthTotals = useMemo(() => {
    const map = new Map<string, number>();
    for (const month of months) map.set(month, 0);
    for (const row of rows)
      for (const point of row.points)
        map.set(point.month, (map.get(point.month) ?? 0) + (point.value ?? 0));
    return map;
  }, [rows, months]);

  const grandTotal = useMemo(
    () => rows.reduce((sum, row) => sum + row.yearTotal, 0),
    [rows]
  );

  // Budget roll-ups (recomputed live as the user types). Each returns null when
  // no cell in its scope carries a budget, matching the actual-side em-dash.
  const sumBudget = (pairs: [string, string][]): number | null => {
    let sum = 0;
    let has = false;
    for (const [rowKey, month] of pairs) {
      const value = getBudgetDollars?.(rowKey, month) ?? null;
      if (value !== null) {
        sum += value;
        has = true;
      }
    }
    return has ? sum : null;
  };
  const accountYearBudget = (accountId: string) =>
    sumBudget(months.map((month) => [acctRowKey(accountId), month]));
  const allMonthBudget = (month: string) =>
    sumBudget(rows.map((row) => [acctRowKey(row.bankAccount.id), month]));
  const grandBudget = () =>
    sumBudget(rows.flatMap((row) => months.map((month) => [acctRowKey(row.bankAccount.id), month] as [string, string])));

  return (
    <div className="overflow-x-auto border border-gray-200 rounded-lg bg-white">
      <table className={`${budgetMode ? "min-w-[2600px]" : "min-w-[1280px]"} w-full text-sm border-collapse`}>
        <thead>
          <tr className="text-xs text-gray-400 uppercase border-b border-gray-200">
            <th className="sticky left-0 z-20 bg-white text-left font-medium px-3 py-2 min-w-[16rem]">
              Account
            </th>
            {months.map((month) =>
              budgetMode ? (
                <th key={month} colSpan={3} className="px-3 py-2 text-right font-medium whitespace-nowrap border-l border-gray-100">
                  <span className="tabular-nums">{monthLabel(month)}</span>
                  <span className="ml-2 text-[10px] normal-case text-gray-400">act · plan · Δ</span>
                </th>
              ) : (
                <th key={month} className="px-3 py-2 text-right font-medium tabular-nums whitespace-nowrap">
                  {monthLabel(month)}
                </th>
              )
            )}
            <th
              colSpan={budgetMode ? 3 : 1}
              className="px-3 py-2 text-right font-semibold tabular-nums whitespace-nowrap text-gray-500 border-l border-gray-100"
            >
              Total
            </th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => {
            const label = accountLabel(row.bankAccount);
            const rowKey = acctRowKey(row.bankAccount.id);
            return (
              <tr key={row.bankAccount.id} className="border-b border-gray-100 hover:bg-gray-50/60">
                <td className="sticky left-0 z-10 bg-white px-3 py-1.5 min-w-[16rem]">
                  <span className="font-medium text-gray-800 truncate">{label}</span>
                </td>
                {row.points.map((point) => (
                  <Fragment key={point.month}>
                    <Cell
                      value={point.value ?? 0}
                      budgetMode={budgetMode}
                      onClick={() =>
                        onCell({
                          kind: "account-month",
                          accountId: row.bankAccount.id,
                          label: `${label} · ${monthLabel(point.month)}`,
                          month: point.month,
                        })
                      }
                    />
                    {budgetMode && getBudgetText && onBudgetChange && (
                      <BudgetCells
                        actual={point.value ?? 0}
                        text={getBudgetText(rowKey, point.month)}
                        onChange={(text) => onBudgetChange(rowKey, point.month, text)}
                        side="REVENUE"
                      />
                    )}
                  </Fragment>
                ))}
                <Cell
                  value={row.yearTotal}
                  strong
                  budgetMode={budgetMode}
                  onClick={() =>
                    onCell({
                      kind: "account-year",
                      accountId: row.bankAccount.id,
                      label: `${label} · full year`,
                    })
                  }
                />
                {budgetMode && (
                  <BudgetSummaryCells actual={row.yearTotal} budget={accountYearBudget(row.bankAccount.id)} side="REVENUE" />
                )}
              </tr>
            );
          })}

          <tr className="border-t-2 border-gray-200 bg-gray-50 font-semibold text-gray-800">
            <td className="sticky left-0 z-10 bg-gray-50 px-3 py-1.5 min-w-[16rem]">
              All accounts
            </td>
            {months.map((month) => (
              <Fragment key={month}>
                <Cell
                  value={monthTotals.get(month) ?? 0}
                  strong
                  budgetMode={budgetMode}
                  onClick={() => onCell({ kind: "all-month", label: `All accounts · ${monthLabel(month)}`, month })}
                />
                {budgetMode && (
                  <BudgetSummaryCells actual={monthTotals.get(month) ?? 0} budget={allMonthBudget(month)} side="REVENUE" />
                )}
              </Fragment>
            ))}
            <Cell
              value={grandTotal}
              strong
              budgetMode={budgetMode}
              onClick={() => onCell({ kind: "all-year", label: "All accounts · full year" })}
            />
            {budgetMode && <BudgetSummaryCells actual={grandTotal} budget={grandBudget()} side="REVENUE" />}
          </tr>
        </tbody>
      </table>
    </div>
  );
}

function Cell({
  value,
  onClick,
  strong,
  budgetMode,
}: {
  value: number;
  onClick: () => void;
  strong?: boolean;
  budgetMode?: boolean;
}) {
  return (
    <td className={`px-3 py-1.5 text-right tabular-nums whitespace-nowrap ${strong ? "font-semibold" : ""} ${budgetMode ? "border-l border-gray-100" : ""}`}>
      {value <= 0 ? (
        <span className="text-gray-300">—</span>
      ) : (
        <CellLink onClick={onClick} title="View the revenue transactions behind this amount">
          {money(value)}
        </CellLink>
      )}
    </td>
  );
}
